import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useMaterialUIController } from "../../context";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useSelector } from "react-redux";
import { useWebPermissions } from "../../hooks/useWebPermissions";
import { SLIDE_TYPE_MODULE } from "../../constants/webPermissions";
import {
  findTitleFilterKey,
  titleFilterKey,
  titleKeysMatch,
} from "./catalogTitleFilter";
import {
  AddSlideCall,
  AddSubTitlesCall,
  AddTitlesCall,
  DeleteSubTitlesCall,
  DeleteTitlesCall,
  EditSubTitlesCall,
  EditTitlesCall,
  GetTitlesCall,
  GetTitlesCallNoStore,
  SwapTitlesCall,
  EditTitleImageCall,
  UpdateTitleLockCall,
  UpdateTitleHiddenCall,
} from "../../redux/api/title.api";
import {
  translateTitleCall,
  translateSubTitleCall,
} from "../../redux/api/titleTranslation.api";
import {
  collectSlidesFromSubtitle,
  collectSlidesFromTitle,
  translatePinsForSlideEntries,
} from "../../redux/api/pinTranslation.api";
import TranslateConfirmDialog from "components/TranslateConfirmDialog";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { Chip, CircularProgress, Stack, Typography, ListItemIcon, ListItemText } from "@mui/material";
import Icon from "@mui/material/Icon";
import { isEmpty } from "lodash";
import SlideCardThumb from "./components/SlideCardThumb";
import SlideThumbnailPickerModal from "./components/SlideThumbnailPickerModal";
import { LoadingIframe, LoadingImage } from "./components/CoverLoader";
import { useNavigate, useSearchParams } from "react-router-dom";
import MDSnackbar from "components/MDSnackbar";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { openAppConfirm } from "../../components/AppDialogs";
import CloseIcon from "@mui/icons-material/Close";
import EditableTitle from "components/EditableTitle";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { basicURL } from "../../apiManager/APIConfig";
import { UploadMyFileRecordCall } from "../../redux/api/uploads.api";
import "@google/model-viewer";
import SubtitleReferencedSlidesDnD from "../myLibrary/components/SubtitleReferencedSlidesDnD";
import CatalogLibraryShell from "./CatalogLibraryShell";

/** Turn a relative storage path into an absolute URL (same host as the API). */
function absoluteAssetUrl(url) {
  if (url == null || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:")) return trimmed;
  const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return trimmed.startsWith("/") ? `${base}${trimmed}` : `${base}/${trimmed}`;
}

function isRasterAssetUrl(value) {
  const blob = String(value || "").toLowerCase();
  if (!blob) return false;
  if (blob.startsWith("data:image/")) return true;
  return /\.(jpe?g|png|webp|gif)(\?|#|$)/i.test(blob);
}

function isModelAssetUrl(value) {
  const blob = String(value || "").toLowerCase();
  return /\.(glb|gltf)(\?|#|$)/i.test(blob);
}

function collectSlideFiles(slide, subtitle) {
  const bags = [
    slide?.Files,
    slide?.files,
    subtitle?.Files,
    subtitle?.files,
  ];
  const out = [];
  bags.forEach((list) => {
    if (Array.isArray(list)) out.push(...list.filter(Boolean));
  });
  return out;
}

/**
 * Card cover photo for anatomy / 3D models — prefer an explicit subtitle
 * image or any raster file attached to the slide (same rule as mobile
 * `rasterCoverFromSubtitle`). Deep-zoom tile probing will never find a
 * cover for .glb/.zip packages.
 */
function getRasterCoverUrl(subtitle, slide) {
  const direct = String(subtitle?.image || "").trim();
  if (direct && !/\.(glb|gltf|zip)(\?|#|$)/i.test(direct)) {
    const abs = absoluteAssetUrl(direct);
    if (abs) return abs;
  }
  const raster = collectSlideFiles(slide, subtitle).find((file) => {
    const ref = file?.url || file?.path || file?.name || "";
    const mime = String(file?.type || "").toLowerCase();
    return mime.startsWith("image/") || isRasterAssetUrl(ref);
  });
  return absoluteAssetUrl(raster?.url || raster?.path || null);
}

/** Prefer a .glb/.gltf file over a parent .zip for model-viewer. */
function getNormalizedModelUrl(slide, subtitle = null) {
  const files = collectSlideFiles(slide, subtitle);
  if (!files.length) return null;
  const ranked = [...files].sort((a, b) => {
    const score = (file) => {
      const ref = String(file?.url || file?.path || file?.name || "").toLowerCase();
      if (/\.glb(\?|#|$)/.test(ref)) return 0;
      if (/\.gltf(\?|#|$)/.test(ref)) return 1;
      if (/\.zip(\?|#|$)/.test(ref)) return 3;
      return 2;
    };
    return score(a) - score(b);
  });
  const pick = ranked.find((file) => {
    const ref = String(file?.url || file?.path || file?.name || "");
    return isModelAssetUrl(ref) || /\.zip(\?|#|$)/i.test(ref);
  }) || ranked[0];
  return absoluteAssetUrl(pick?.url || pick?.path || null);
}

function Titles({
  type,
  readOnly = false,
  library_category = null,
  embedded = false,
  hideToolbar = false,
  searchQuery: searchQueryProp = null,
  onSearchQueryChange = null,
  addFromLibraryNode = null,
  itemsFromLibrary = null,
  onRemoveLibraryRef = null,
  onLibraryRefresh = null,
  /** (targetSubtitleId, orderedSlideIds) — My Library only; updates per-user link order, not public slides */
  onReorderLibraryRefs = null,
  /** "grid" | "list" — controls slide card layout inside each folder */
  viewMode: viewModeProp = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const [folderMenu, setFolderMenu] = useState({ anchorEl: null, itemId: null });
  const [slideMenu, setSlideMenu] = useState({
    anchorEl: null,
    subtitleId: null,
    mode: "delete", // "delete" | "unlink"
    publicTitleId: null,
    slideId: null,
  });
  const embeddedAccent = "#0061FE";
  const cardSurfaceBg = darkMode ? "rgba(255,255,255,0.06)" : "background.paper";
  const cardBorder = darkMode ? "rgba(255,255,255,0.12)" : "divider";
  const catalogPair =
    !embedded && !library_category
      ? { his: "three_d_model", emb: "emb_3d_model" }[type] || null
      : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const segment =
    catalogPair && searchParams.get("segment") === "models" ? "models" : "slides";
  const activeType = segment === "models" && catalogPair ? catalogPair : type;
  const slideType = activeType;
  const three_d_model =
    activeType === "three_d_model" ||
    activeType === "emb_3d_model" ||
    activeType === "anatomy" ||
    library_category === "three_d_model" ||
    library_category === "emb_3d_model" ||
    library_category === "anatomy";
  const initialItems = useSelector((state) => {
    const st = state?.title;
    if (!st) return [];
    return Array.isArray(st.titles) ? st.titles : [];
  });
  const lang = useSelector((state) => state.general?.lang);
  const role = useSelector((state) => state?.auth?.role);
  const normalizedRole = String(role || "").toLowerCase();
  const showAiTranslate = !["university_admin", "prof", "professor"].includes(
    normalizedRole
  );
  const { isPlatformAdmin, isCustomUser, canAccessModule, canForSlideType } = useWebPermissions();
  const isAdmin = isPlatformAdmin;
  const canAddSlides = isPlatformAdmin || canForSlideType(slideType, "slides_add");
  const canEditSlides = isPlatformAdmin || canForSlideType(slideType, "slides_edit");
  const canDeleteSlides =
    isPlatformAdmin || canForSlideType(slideType, "slides_delete");
  const canWriteCatalogue =
    !readOnly &&
    (embedded ||
      canAddSlides ||
      canEditSlides ||
      canDeleteSlides);
  const includeHiddenInList = isPlatformAdmin || canEditSlides;
  /*
    Lock toggle is only meaningful on the core catalogue
    categories (histology, embryology, radiology, 3D, anatomy) — these
    are the screens the mobile app paywalls based on the
    `subtitles[].locked` flag. We hide the lock UI everywhere else
    (presentations, library views, embedded pickers) so admins don't
    accidentally flip flags that nothing reads.
  */
  const LOCK_TOGGLE_TYPES = [
    "his",
    "emb",
    "rad",
    "three_d_model",
    "emb_3d_model",
    "anatomy",
  ];
  const showLockControls =
    isAdmin && !readOnly && LOCK_TOGGLE_TYPES.includes(slideType);
  /*
    Hide toggle uses the same scope as lock — only meaningful on the
    five core public catalogues (his / emb / 3d / emb_3d / anatomy).
    Hidden rows are filtered out for non-admin viewers on both web
    and mobile via `titles.is_hidden` on the server.
  */
  const showHideControls =
    isAdmin && !readOnly && LOCK_TOGGLE_TYPES.includes(slideType);
  const [lockPending, setLockPending] = useState({}); // { [subtitleId]: true }
  const [hidePending, setHidePending] = useState({}); // { [titleOrSubtitleId]: true }
  /** Thumbnail menu + pick-from-slide modal (catalogue + My collection). */
  const [thumbMenu, setThumbMenu] = useState({
    anchorEl: null,
    subtitleId: null,
    slide: null,
  });
  const [thumbPicker, setThumbPicker] = useState({
    open: false,
    subtitleId: null,
    slide: null,
  });
  const [thumbSaving, setThumbSaving] = useState(false);
  const thumbUploadInputRef = useRef(null);
  /*
    Per-row AI-translate confirmation dialog targets. We keep the
    full title / subtitle object so the dialog can show its current
    text in the warning copy without an extra fetch. Only one is
    open at a time — they're driven by separate keys so a parent
    title and one of its subtitles can't both be open.
  */
  const [translateTitleTarget, setTranslateTitleTarget] = useState(null);
  const [translateSubTitleTarget, setTranslateSubTitleTarget] = useState(null);
  const [translateTitlePinsTarget, setTranslateTitlePinsTarget] = useState(null);
  const [translateSubTitlePinsTarget, setTranslateSubTitlePinsTarget] =
    useState(null);
  const showPinTranslateControls =
    activeType === "his" || activeType === "emb" || activeType === "rad";

  // Defensive cleanup: strip stray ")}" text nodes that sometimes appear
  // between slide cards due to legacy content. This watcher keeps the DOM tidy
  // without affecting React state.
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const sanitizeNode = (node) => {
      if (!node || node.nodeType !== Node.TEXT_NODE) return;
      const value = node.nodeValue;
      if (value && value.includes(")}")) {
        node.nodeValue = value.replace(/\)\}/g, "");
      }
    };

    // Initial pass
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n = walker.nextNode();
    while (n) {
      sanitizeNode(n);
      n = walker.nextNode();
    }

    // Observe future changes
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData") {
          sanitizeNode(m.target);
        }
        if (m.type === "childList") {
          m.addedNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              sanitizeNode(child);
            } else if (child.nodeType === Node.ELEMENT_NODE) {
              const walkerInner = document.createTreeWalker(child, NodeFilter.SHOW_TEXT);
              let t = walkerInner.nextNode();
              while (t) {
                sanitizeNode(t);
                t = walkerInner.nextNode();
              }
            }
          });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const rawItems = Array.isArray(initialItems) ? initialItems : [];
  // Treat missing library_category as "his" so existing private titles still show (e.g. in Histology tab)
  const filteredByCategory = library_category
    ? rawItems.filter((i) => (i.library_category || "his") === library_category)
    : rawItems;
  const [items, setItems] = useState(filteredByCategory);
  const [searchQueryLocal, setSearchQueryLocal] = useState("");
  const searchQuery = searchQueryProp != null ? searchQueryProp : searchQueryLocal;
  const setSearchQuery = onSearchQueryChange || setSearchQueryLocal;
  const [titleFilter, setTitleFilter] = useState(null);
  const [catalogViewMode, setCatalogViewMode] = useState("grid");
  const effectiveViewMode = viewModeProp || catalogViewMode;
  const isListView = effectiveViewMode === "list";
  const [catalogSortBy, setCatalogSortBy] = useState("newest");
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [pairedItems, setPairedItems] = useState([]);
  const displayItems = (embedded && itemsFromLibrary != null)
    ? (library_category ? itemsFromLibrary.filter((i) => (i.library_category || "his") === library_category) : itemsFromLibrary)
    : items;
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [message, setMessage] = useState("");
  // const [lang, setLanguage] = useState("en");

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  /** Swap order on server + Redux; embedded My Library also reloads props from GET /library. */
  const swapTitlesAndRefreshLibrary = useCallback(
    async (swapBody) => {
      await SwapTitlesCall(swapBody);
      if (embedded && typeof onLibraryRefresh === "function") {
        await onLibraryRefresh();
      }
    },
    [embedded, onLibraryRefresh]
  );

  /*
    Admin opts in to `?include_hidden=true` so the management page can
    still render the "Hidden" badge / toggle on hidden rows. Non-admin
    callers (e.g. a prof browsing this page for their own private
    titles) get the same filtered list the mobile app sees.
  */
  const fetchTitles = async () => {
    try {
      await GetTitlesCall(lang, activeType, includeHiddenInList);
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    setItems([]);
    fetchTitles();
  }, [activeType, lang, includeHiddenInList]);

  useEffect(() => {
    setTitleFilter(null);
  }, [type]);

  useEffect(() => {
    if (!catalogPair || !isCustomUser || searchParams.get("segment")) return undefined;
    const slidesModule = SLIDE_TYPE_MODULE[type];
    const modelsModule = SLIDE_TYPE_MODULE[catalogPair];
    if (!canAccessModule(slidesModule) && canAccessModule(modelsModule)) {
      const next = new URLSearchParams(searchParams);
      next.set("segment", "models");
      setSearchParams(next, { replace: true });
    }
    return undefined;
  }, [canAccessModule, catalogPair, isCustomUser, searchParams, setSearchParams, type]);

  useEffect(() => {
    if (!catalogPair) {
      setPairedItems([]);
      return undefined;
    }
    let alive = true;
    const otherType = activeType === type ? catalogPair : type;
    GetTitlesCallNoStore(lang, otherType, includeHiddenInList).then((list) => {
      if (alive) setPairedItems(Array.isArray(list) ? list : []);
    });
    return () => {
      alive = false;
    };
  }, [activeType, catalogPair, includeHiddenInList, lang, type]);

  useEffect(() => {
    const raw = Array.isArray(initialItems) ? initialItems : [];
    const filtered = library_category ? raw.filter((i) => (i.library_category || "his") === library_category) : raw;
    setItems(filtered);
  }, [initialItems, library_category]);

  const handleAddTitle = () => {
    /*
      We deliberately don't seed `body` with a placeholder here:
      the `body` Localization is now surfaced as the model
      description on the mobile 3D viewer (via the floating
      "About this model" overlay), so a generated string like
      "Description 7" would pop up to students as if it were
      author-written. The admin fills it in via the inline
      `EditableDescription` once they have something to say.
    */
    AddTitlesCall({
      title: `New Title ${(displayItems || items)?.length + 1}`,
      lang,
      type: activeType,
      ...(library_category && { library_category }),
    }).then(() => { if (embedded && onLibraryRefresh) onLibraryRefresh(); });
  };

  const handleAddSubtitle = (itemId) => {
    AddSubTitlesCall({
      title: `New Subtitle ${(displayItems || items)?.length + 1}`,
      title_id: itemId,
      lang,
      type: activeType,
    }).then(() => { if (embedded && onLibraryRefresh) onLibraryRefresh(); });
  };

  const handleEdit = async (id, newValue, type) => {
    if (type === "MainTitle" || type === "MainBody") {
      const body = {
        id,
        lang,
        type: slideType,
        [type === "MainTitle" ? "title" : "body"]: newValue,
      };
      await EditTitlesCall(body);
    } else {
      const body = {
        id,
        lang,
        type: slideType,
        [type === "SubTitle" ? "title" : "body"]: newValue,
      };
      await EditSubTitlesCall(body);
    }

    if (embedded && typeof onLibraryRefresh === "function") {
      await onLibraryRefresh();
    }
  };

  /*
    Admin-only: flip the lock state on a single subtitle (catalogue
    "slide"). Confirms first because this directly affects what
    paying users see in the mobile app:
      - locked  = true  → only subscribers / university users see it
      - locked  = false → everyone sees it (free)
    On success we let UpdateTitleLockCall refresh the Redux titles
    list so the badge + button icon update without a manual reload.
  */
  const handleToggleLock = async (subtitle) => {
    if (!showLockControls || !subtitle?.id) return;
    const nextLocked = !subtitle.locked;
    const confirmation = await openAppConfirm({
      title: nextLocked ? "Lock this slide?" : "Unlock this slide?",
      message: nextLocked
        ? "Locked slides are only visible to paying / university users in the app. Free users will see a paywall preview."
        : "Unlocked slides are free for every user in the app, including non-subscribers.",
      confirmText: nextLocked ? "Lock" : "Unlock",
      confirmColor: nextLocked ? "warning" : "success",
    });
    if (!confirmation) return;
    setLockPending((prev) => ({ ...prev, [subtitle.id]: true }));
    const res = await UpdateTitleLockCall({
      title_id: subtitle.id,
      locked: nextLocked,
      lang,
      type: activeType,
    });
    setLockPending((prev) => {
      const next = { ...prev };
      delete next[subtitle.id];
      return next;
    });
    if (res?.status && res.status >= 400) {
      setMessage(res?.message || "Failed to update lock state.");
      setTimeout(() => openErrorSB(), 200);
    } else {
      setMessage(nextLocked ? "Slide locked." : "Slide unlocked.");
      setTimeout(() => openSuccessSB(), 200);
    }
  };

  /*
    Admin-only: toggle `is_hidden` on a title or subtitle row.
    Hidden rows are filtered out for non-admin viewers on both web
    and mobile, so this is the way to take catalogue content offline
    without deleting it.

    `entity` is either a parent title (with `subtitles` array) or a
    subtitle row — both work because the backend resolves the root
    type via parent_id walk.
  */
  const handleToggleHidden = async (entity, kind = "title") => {
    if (!showHideControls || !entity?.id) return;
    const nextHidden = !entity.is_hidden;
    const noun = kind === "subtitle" ? "subtitle" : "title";
    const confirmation = await openAppConfirm({
      title: nextHidden ? `Hide this ${noun}?` : `Unhide this ${noun}?`,
      message: nextHidden
        ? `Hidden ${noun}s are removed from the catalogue for everyone except admins (web + mobile). Admins can unhide them at any time.`
        : `Unhidden ${noun}s become visible again to all users in the catalogue.`,
      confirmText: nextHidden ? "Hide" : "Unhide",
      confirmColor: nextHidden ? "warning" : "success",
    });
    if (!confirmation) return;
    setHidePending((prev) => ({ ...prev, [entity.id]: true }));
    const res = await UpdateTitleHiddenCall({
      title_id: entity.id,
      is_hidden: nextHidden,
      lang,
      type: activeType,
    });
    setHidePending((prev) => {
      const next = { ...prev };
      delete next[entity.id];
      return next;
    });
    if (res?.status && res.status >= 400) {
      setMessage(res?.message || "Failed to update hidden state.");
      setTimeout(() => openErrorSB(), 200);
    } else {
      setMessage(
        nextHidden
          ? `${noun.charAt(0).toUpperCase() + noun.slice(1)} hidden.`
          : `${noun.charAt(0).toUpperCase() + noun.slice(1)} unhidden.`
      );
      setTimeout(() => openSuccessSB(), 200);
    }
  };

  const handleDeleteSubTitle = async (id) => {
    const body = {
      id,
      lang,
      type: activeType,
    };
    const confirmation = await openAppConfirm({
      title: "Delete subtitle?",
      message:
        "Are you sure you want to delete this subtitle? All pins, texts and slides inside it will be deleted permanently.",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = await DeleteSubTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The subtitle deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
        if (embedded && typeof onLibraryRefresh === "function") {
          onLibraryRefresh();
        }
      }
    }
  };
  const handleDeleteTitle = async (id) => {
    const body = {
      id,
      lang,
      type: activeType,
    };
    const confirmation = await openAppConfirm({
      title: "Delete title?",
      message:
        "Are you sure you want to delete this title and all its subtitles? All pins, texts and slides inside will be deleted permanently.",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = await DeleteTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The title deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
        if (embedded && typeof onLibraryRefresh === "function") {
          onLibraryRefresh();
        }
      }
    }
  };

  const handleAddMaterial = async (id) => {
    const res = await AddSlideCall({ title_id: id, lang, type });
    if (!res?.slide?.id) return;
    const base = three_d_model
      ? `/showThreeD/${res.slide.id}/${lang}`
      : `/showTitle/${res.slide.id}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const handleViewSlide = (subtitle) => {
    const primary =
      (subtitle?.slides && subtitle.slides[0]) ||
      (subtitle?.referencedSlides && subtitle.referencedSlides[0]) ||
      null;
    const slideId = primary?.id;
    if (!slideId) return;
    const base = three_d_model
      ? `/showThreeD/${slideId}/${lang}`
      : `/showTitle/${slideId}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const handleViewSlideById = (slide) => {
    const slideId = slide?.id;
    if (!slideId) return;
    const base = three_d_model
      ? `/showThreeD/${slideId}/${lang}`
      : `/showTitle/${slideId}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .trim();

  const getLocalizedField = (entity, field) => {
    const list = Array.isArray(entity?.titleLocalizations)
      ? entity.titleLocalizations.filter((e) => e?.field === field)
      : [];
    return list?.[0]?.value || "";
  };

  const titleFilters = useMemo(() => {
    if (!catalogPair) return [];
    const order = [];
    const labels = new Map();
    const add = (raw) => {
      const label = String(raw || "").trim();
      const key = titleFilterKey(label);
      if (!key || findTitleFilterKey(order, key)) return;
      order.push(key);
      labels.set(key, label);
    };
    const consider = (list) => {
      (list || []).forEach((item) => {
        const label = getLocalizedField(item, "title");
        if (titleFilterKey(label)) add(label);
        else (item.subtitles || []).forEach((subtitle) => add(getLocalizedField(subtitle, "title")));
      });
    };
    consider(displayItems);
    consider(pairedItems);
    const countFor = (key) =>
      (Array.isArray(displayItems) ? displayItems : []).reduce((sum, item) => {
        const subs = item.subtitles || [];
        if (titleKeysMatch(titleFilterKey(getLocalizedField(item, "title")), key)) {
          return sum + subs.length;
        }
        return (
          sum +
          subs.filter((subtitle) =>
            titleKeysMatch(titleFilterKey(getLocalizedField(subtitle, "title")), key)
          ).length
        );
      }, 0);
    return order.map((key) => ({
      key,
      label: labels.get(key) || key,
      count: countFor(key),
    }));
  }, [catalogPair, displayItems, pairedItems]);

  const visibleItems = useMemo(() => {
    let list = Array.isArray(displayItems) ? displayItems : [];
    if (titleFilter) {
      list = list.filter((item) => {
        if (titleKeysMatch(titleFilterKey(getLocalizedField(item, "title")), titleFilter)) return true;
        return (item.subtitles || []).some((subtitle) =>
          titleKeysMatch(titleFilterKey(getLocalizedField(subtitle, "title")), titleFilter)
        );
      });
    }
    const q = normalizeText(searchQuery);
    if (q) {
      list = list.filter((item) => {
        const mainTitle = normalizeText(getLocalizedField(item, "title"));
        const mainBody = normalizeText(getLocalizedField(item, "body"));
        const inMain = mainTitle.includes(q) || mainBody.includes(q);
        if (inMain) return true;

        const subtitles = Array.isArray(item?.subtitles) ? item.subtitles : [];
        return subtitles.some((subtitle) => {
          const subTitle = normalizeText(getLocalizedField(subtitle, "title"));
          const subBody = normalizeText(getLocalizedField(subtitle, "body"));
          return subTitle.includes(q) || subBody.includes(q);
        });
      });
    }

    if (catalogPair && catalogSortBy) {
      const sorted = [...list];
      sorted.sort((a, b) => {
        const aTitle = getLocalizedField(a, "title") || "";
        const bTitle = getLocalizedField(b, "title") || "";
        const aTime = new Date(a?.created_at || a?.updated_at || 0).getTime();
        const bTime = new Date(b?.created_at || b?.updated_at || 0).getTime();
        if (catalogSortBy === "az") return aTitle.localeCompare(bTitle);
        if (catalogSortBy === "za") return bTitle.localeCompare(aTitle);
        if (catalogSortBy === "oldest") return aTime - bTime;
        return bTime - aTime; // newest
      });
      return sorted;
    }
    return list;
  }, [displayItems, searchQuery, titleFilter, catalogPair, catalogSortBy]);

  const catalogAllCount = useMemo(
    () =>
      (Array.isArray(displayItems) ? displayItems : []).reduce(
        (sum, item) => sum + (item.subtitles?.length || 0),
        0
      ),
    [displayItems]
  );

  const catalogPageTitle =
    type === "emb" ? "Embryology" : type === "his" ? "Histology" : "Library";
  const catalogPageSubtitle =
    segment === "models"
      ? "Browse and manage 3D models across categories."
      : "Manage your slides, organize them into topics and share with your students.";

  const catalogAccent =
    type === "emb"
      ? segment === "models"
        ? "#06B6D4"
        : "#0EA5E9"
      : segment === "models"
        ? "#7C3AED"
        : "#5B4BFF";

  const selectCatalogSegment = (next) => {
    const nextSegment = next === "models" ? "models" : "slides";
    if (nextSegment === segment) return;
    setCatalogLoading(true);
    setItems([]);
    setTitleFilter(null);
    const params = new URLSearchParams(searchParams);
    if (next === "models") params.set("segment", "models");
    else params.delete("segment");
    setSearchParams(params, { replace: true });
  };

  const applySubtitleThumbnail = async (subtitleId, imageUrl) => {
    if (!subtitleId || !imageUrl) return;
    await EditTitleImageCall({
      id: subtitleId,
      lang,
      type: slideType,
      image: imageUrl,
    });
    if (embedded && typeof onLibraryRefresh === "function") {
      await onLibraryRefresh();
    }
    setMessage("Thumbnail updated.");
    setSuccessSB(true);
  };

  const handleAddThumbNail = async (event, id) => {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = "";
    if (!file || !id) return;
    if (!/^image\//i.test(file.type) && !/\.(png|jpe?g|webp|gif)$/i.test(file.name || "")) {
      setMessage("Please choose a PNG, JPG, or WebP image.");
      setErrorSB(true);
      return;
    }
    try {
      setThumbSaving(true);
      const uploaded = await UploadMyFileRecordCall(file, "title_thumbnail");
      const thumbnail = uploaded?.url;
      if (!thumbnail) throw new Error("Upload succeeded but no image URL was returned.");
      await applySubtitleThumbnail(id, thumbnail);
    } catch (error) {
      console.error("Thumbnail upload error:", error);
      setMessage(error?.message || "Thumbnail upload failed.");
      setErrorSB(true);
    } finally {
      setThumbSaving(false);
    }
  };

  const openThumbMenu = (event, subtitleId, slide) => {
    event.stopPropagation();
    setThumbMenu({
      anchorEl: event.currentTarget,
      subtitleId,
      slide: slide || null,
    });
  };

  const closeThumbMenu = () => {
    setThumbMenu({ anchorEl: null, subtitleId: null, slide: null });
  };

  const renderItem = (item, index, list = items) => {
    const filteredTitle = item?.titleLocalizations?.filter(
      (e) => e.field == "title"
    );
    const filteredBody = item?.titleLocalizations?.filter(
      (e) => e.field == "body"
    );

    const rawFolderTitle = String(filteredTitle?.[0]?.value || "").trim();
    const title =
      rawFolderTitle && !/^click to edit/i.test(rawFolderTitle)
        ? rawFolderTitle
        : embedded
          ? "Untitled folder"
          : "Click to edit the title";
    const body = filteredBody?.[0]?.value || (embedded ? "" : "Click to edit the body");
    const gapPx = 15;
    const showGrid = embedded || (item?.subtitles?.length > 0);
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        sx={{
          mb: 0,
          mt: index > 0 ? (embedded ? "20px" : "25px") : 0,
          px: embedded ? 0 : `${gapPx}px`,
          pt: 0,
          pb: 0.5,
          width: "100%",
          minWidth: 0,
          ...(embedded
            ? {
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "#F8FAFC",
                border: "1px solid",
                borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(148,163,184,0.28)",
                boxShadow: "none",
              }
            : {}),
        }}
      >
          {!embedded && canWriteCatalogue && (
            <MDBox display="flex" gap={0.5} sx={{ mb: `${gapPx}px` }}>
              {index !== 0 && (
                <Tooltip title="Move up">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const body = {
                        current_id: item?.id,
                        goal_id: list?.[index - 1]?.id,
                        lang,
                        type: activeType,
                      };
                      swapTitlesAndRefreshLibrary(body);
                    }}
                    sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                  >
                    <ArrowDropUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {list?.length !== index + 1 && (
                <Tooltip title="Move down">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const body = {
                        current_id: item?.id,
                        goal_id: list?.[index + 1]?.id,
                        lang,
                        type: activeType,
                      };
                      swapTitlesAndRefreshLibrary(body);
                    }}
                    sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                  >
                    <ArrowDropDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          )}

          {catalogPair ? (
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", color: "#94a3b8", mb: 0.75 }}>
              CATEGORY
            </Typography>
          ) : null}

          {embedded ? (
            <MDBox
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
              width="100%"
              sx={{ mb: 1.5 }}
            >
              <MDBox display="flex" alignItems="flex-start" sx={{ minWidth: 0, flex: "1 1 200px" }}>
                <MDBox sx={{ minWidth: 0, flex: 1 }}>
                  <EditableTitle
                    title={title}
                    titleFont="titleCatalog"
                    align="left"
                    fullWidth
                    onEdit={(newTitle) => handleEdit(item.id, newTitle, "MainTitle")}
                    readOnly={readOnly}
                  />
                  <EditableDescription
                    description={item?.titleLocalizations?.find((e) => e?.field === "body")?.value || ""}
                    onEdit={(newValue) => handleEdit(item.id, newValue, "MainBody")}
                    readOnly={readOnly}
                    variant="titleCatalog"
                    align="left"
                  />
                </MDBox>
              </MDBox>
              {canWriteCatalogue && (
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {typeof addFromLibraryNode === "function" ? addFromLibraryNode(item) : addFromLibraryNode}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderMenu({ anchorEl: e.currentTarget, itemId: item.id });
                    }}
                    sx={{
                      border: "1px solid",
                      borderColor: darkMode ? "rgba(255,255,255,0.12)" : alpha("#0f172a", 0.1),
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      bgcolor: darkMode ? "transparent" : "#ffffff",
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              )}
            </MDBox>
          ) : (
            <>
          <EditableTitle
            title={title}
            titleFont={catalogPair ? "titleCatalog" : "title"}
            align={catalogPair ? "left" : "center"}
            onEdit={(newTitle) => handleEdit(item.id, newTitle, "MainTitle")}
            readOnly={readOnly}
          />
          {/* Description / "about this title" body. The mobile app
              renders this string in the floating overlay above the
              3D viewer, so admins can use it to set context for the
              whole catalogue node (e.g. "Histology — connective
              tissues" gets a paragraph explaining the chapter). */}
          <EditableDescription
            description={item?.titleLocalizations?.find((e) => e?.field === "body")?.value || ""}
            onEdit={(newValue) => handleEdit(item.id, newValue, "MainBody")}
            readOnly={readOnly}
            variant={catalogPair ? "titleCatalog" : "title"}
            align={catalogPair ? "left" : "center"}
          />
          {canWriteCatalogue && (
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} width="100%" sx={{ mt: `${gapPx}px` }}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDButton
                  color="primary"
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => handleAddSubtitle(item.id)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: catalogPair ? 999 : 3,
                    px: 2,
                    fontSize: catalogPair ? 15 : undefined,
                    boxShadow: "none",
                    "&:hover": { boxShadow: theme.shadows[2] },
                  }}
                >
                  Add Subtitle
                </MDButton>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={1}>
                {catalogPair ? (
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.secondary", mr: 0.5, whiteSpace: "nowrap" }}>
                    {item?.subtitles?.length || 0} {segment === "models" ? "models" : "slides"}
                  </Typography>
                ) : null}
                {showAiTranslate && (
                <Tooltip title="AI Translate — translate this title and its description into every supported language. Re-running overwrites prior translations.">
                  <span>
                    <MDButton
                      color="primary"
                      variant="outlined"
                      startIcon={<TranslateRoundedIcon />}
                      onClick={() => setTranslateTitleTarget(item)}
                      sx={{
                        fontSize: catalogPair ? 15 : undefined,
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: catalogPair ? 999 : 3,
                        borderWidth: 1.5,
                        "&:hover": { borderWidth: 1.5 },
                      }}
                    >
                      AI Translate
                    </MDButton>
                  </span>
                </Tooltip>
                )}
                {showAiTranslate && showPinTranslateControls && (
                  <Tooltip title="AI Translate all pins on every slide under this title into every supported language. Existing pin translations are updated.">
                    <span>
                      <MDButton
                        color="info"
                        variant="outlined"
                        startIcon={<PushPinOutlinedIcon />}
                        onClick={() => setTranslateTitlePinsTarget(item)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 3,
                          borderWidth: 1.5,
                          "&:hover": { borderWidth: 1.5 },
                        }}
                      >
                        Translate pins
                      </MDButton>
                    </span>
                  </Tooltip>
                )}
                {showHideControls && (
                  <Tooltip
                    title={
                      item?.is_hidden
                        ? "Unhide — show this title to all users again"
                        : "Hide — remove this title from non-admin views"
                    }
                  >
                    <span>
                      <MDButton
                        color={item?.is_hidden ? "warning" : "info"}
                        variant="outlined"
                        startIcon={
                          <Icon>
                            {item?.is_hidden ? "visibility_off" : "visibility"}
                          </Icon>
                        }
                        onClick={() => handleToggleHidden(item, "title")}
                        disabled={!!hidePending[item.id]}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: catalogPair ? 15 : undefined,
                          borderRadius: catalogPair ? 999 : 3,
                          borderWidth: 1.5,
                          "&:hover": { borderWidth: 1.5 },
                        }}
                      >
                        {item?.is_hidden ? "Unhide" : "Hide"}
                      </MDButton>
                    </span>
                  </Tooltip>
                )}
                <MDButton
                  color="error"
                  variant="outlined"
                  onClick={() => handleDeleteTitle(item.id)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: catalogPair ? 15 : undefined,
                    borderRadius: catalogPair ? 999 : 3,
                    borderWidth: 1.5,
                    "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06) },
                  }}
                >
                  Delete
                </MDButton>
              </MDBox>
            </MDBox>
          )}
            </>
          )}

          {showGrid && (
            <MDBox
              sx={{
                mt: embedded ? 0.5 : "25px",
                display: "grid",
                // List = single column rows; grid = fluid multi-column tiles.
                gridTemplateColumns: isListView
                  ? "1fr"
                  : {
                      xs: "1fr",
                      sm: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))",
                      md: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
                    },
                gap: { xs: 1.5, sm: 2 },
                width: "100%",
                alignItems: "stretch",
              }}
            >
              {embedded && canWriteCatalogue ? (
                <MDBox
                  onClick={() => handleAddSubtitle(item.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAddSubtitle(item.id);
                    }
                  }}
                  sx={{
                    minHeight: isListView ? 72 : { xs: 200, sm: 220 },
                    height: "100%",
                    borderRadius: 3,
                    border: "2px dashed",
                    borderColor: alpha(embeddedAccent, 0.35),
                    bgcolor: alpha(embeddedAccent, darkMode ? 0.08 : 0.04),
                    display: "flex",
                    flexDirection: isListView ? "row" : "column",
                    alignItems: "center",
                    justifyContent: isListView ? "flex-start" : "center",
                    gap: 1,
                    px: 2,
                    py: isListView ? 1.25 : 0,
                    cursor: "pointer",
                    textAlign: isListView ? "left" : "center",
                    transition: "border-color 160ms ease, background 160ms ease",
                    "&:hover": {
                      borderColor: embeddedAccent,
                      bgcolor: alpha(embeddedAccent, darkMode ? 0.14 : 0.08),
                    },
                  }}
                >
                  <MDBox
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(embeddedAccent, 0.12),
                      color: embeddedAccent,
                    }}
                  >
                    <AddRoundedIcon />
                  </MDBox>
                  <Typography sx={{ fontWeight: 800, color: embeddedAccent, fontSize: 15 }}>
                    {t("pages.myLibrary.addNewSlide")}
                  </Typography>
                  {!isListView ? (
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 180, lineHeight: 1.4 }}>
                      {t("pages.myLibrary.addNewSlideHint")}
                    </Typography>
                  ) : null}
                </MDBox>
              ) : null}
              {(item.subtitles || []).map((subtitle, idx) => {
              const ownSlides = subtitle?.slides || [];
              const refSlides = subtitle?.referencedSlides || [];
              const primarySlide = ownSlides[0] || refSlides[0] || null;
              const hasAnySlide = !!primarySlide;
              const label = hasAnySlide ? "View" : "Add Material";
              // Microlab library links have referencedSlides only — never hard-delete those.
              // User-added slides (own slides) and empty new subtitles can be deleted.
              const isLibraryLinkedOnly =
                ownSlides.length === 0 && refSlides.length > 0;
              const canDeleteUserSlideCard =
                ownSlides.length > 0 || (!isLibraryLinkedOnly && refSlides.length === 0);
              const primaryLibraryRef = isLibraryLinkedOnly ? refSlides[0] : null;
              const showEmbeddedSlideMenu =
                embedded &&
                canWriteCatalogue &&
                (canDeleteUserSlideCard ||
                  (isLibraryLinkedOnly &&
                    typeof onRemoveLibraryRef === "function" &&
                    primaryLibraryRef?.public_title_id));

              const filteredTitle = subtitle?.titleLocalizations?.filter(
                (e) => e.field == "title"
              );
              const filteredBody = subtitle?.titleLocalizations?.filter(
                (e) => e.field == "body"
              );

              const localizationTitle = String(filteredTitle?.[0]?.value || "").trim();
              const isPlaceholderTitle = /^click to edit/i.test(localizationTitle);
              // Library-linked cards often have an empty private subtitle name;
              // prefer our-library display name, then Microlab public slide title.
              const linkedPublicName = String(
                primarySlide?.libraryDisplayTitle ||
                  primarySlide?.slide_title ||
                  primarySlide?.public_slide_title ||
                  (primarySlide?.slideLocalizations || primarySlide?.slide_localizations || []).find(
                    (l) => l?.field === "title"
                  )?.value ||
                  ""
              ).trim();
              const title =
                (!isPlaceholderTitle && localizationTitle) ||
                (isLibraryLinkedOnly ? linkedPublicName : "") ||
                (embedded ? "Untitled" : "Click to edit the sub title");
              const body =
                filteredBody?.[0]?.value || (embedded ? "" : "Click to edit the sub body");
              return (
                <React.Fragment key={subtitle.id}>
                <MDBox
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    height: "100%",
                    position: "relative",
                    display: "flex",
                  }}
                >
                  {showEmbeddedSlideMenu ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSlideMenu({
                          anchorEl: e.currentTarget,
                          subtitleId: subtitle.id,
                          mode: isLibraryLinkedOnly ? "unlink" : "delete",
                          publicTitleId: primaryLibraryRef?.public_title_id || null,
                          slideId: primaryLibraryRef?.id || null,
                        });
                      }}
                      sx={{
                        position: "absolute",
                        top: isListView ? 12 : 10,
                        right: 10,
                        zIndex: 4,
                        width: 32,
                        height: 32,
                        bgcolor: "rgba(255,255,255,0.92)",
                        border: "1px solid rgba(148,163,184,0.35)",
                        boxShadow: "0 4px 12px rgba(15,23,42,0.12)",
                        color: "ink.body",
                        "&:hover": { bgcolor: "background.card" },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : null}
                  {/* Lock-status badge (admin-only, catalogue slides only).
                      Sits on top-left of the card, above the thumbnail.
                      Green "Free" / amber "Locked" gives admins an at-a-
                      glance view of what's behind the paywall. */}
                  {showLockControls && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 2,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: catalogPair ? "0.78rem" : "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        backdropFilter: "blur(6px)",
                        backgroundColor: subtitle?.locked
                          ? alpha(theme.palette.warning.main, 0.92)
                          : alpha(theme.palette.success.main, 0.92),
                        color: "#fff",
                        boxShadow: theme.shadows[2],
                        pointerEvents: "none",
                      }}
                    >
                      <Icon sx={{ fontSize: "0.9rem !important" }}>
                        {subtitle?.locked ? "lock" : "lock_open"}
                      </Icon>
                      {subtitle?.locked ? "Locked" : "Free"}
                    </MDBox>
                  )}
                  {/* Hidden-status badge (admin-only). Only shown when
                      the subtitle is currently hidden — gives admins
                      a clear at-a-glance signal that this card is
                      invisible to everyone else. */}
                  {showHideControls && subtitle?.is_hidden && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: catalogPair ? "0.78rem" : "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        backdropFilter: "blur(6px)",
                        backgroundColor: alpha(theme.palette.warning.main, 0.92),
                        color: "#fff",
                        boxShadow: theme.shadows[2],
                        pointerEvents: "none",
                      }}
                    >
                      <Icon sx={{ fontSize: "0.9rem !important" }}>
                        visibility_off
                      </Icon>
                      Hidden
                    </MDBox>
                  )}
                  <Card
                    variant="outlined"
                    sx={{
                      pt: 0,
                      px: 0,
                      pb: isListView ? 0 : `${gapPx}px`,
                      display: "flex",
                      flexDirection: isListView ? "row" : "column",
                      alignItems: isListView ? "stretch" : "stretch",
                      flex: 1,
                      width: "100%",
                      minWidth: 0,
                      height: "100%",
                      borderRadius: 3,
                      borderColor: cardBorder,
                      bgcolor: cardSurfaceBg,
                      color: "text.main",
                      boxShadow: theme.shadows[1],
                      overflow: "hidden",
                      cursor: "default",
                      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": { boxShadow: theme.shadows[4], borderColor: theme.palette.divider },
                      "& > *:first-of-type": {
                        margin: 0,
                        padding: 0,
                        width: isListView ? undefined : "100%",
                        maxWidth: isListView ? undefined : "100%",
                      },
                    }}
                  >
                    {(() => {
                      const coverUrl = getRasterCoverUrl(subtitle, primarySlide);
                      const modelUrl =
                        three_d_model && primarySlide
                          ? getNormalizedModelUrl(primarySlide, subtitle)
                          : null;
                      const thumbH = isListView ? 88 : catalogPair ? 158 : 126;
                      const thumbW = isListView ? 120 : "100%";
                      /*
                        In My collection (embedded), open the slide from the
                        thumbnail only — subtitle title/description stay
                        clickable for inline edit.
                      */
                      const openSlideFromThumb = embedded
                        ? () =>
                            readOnly || hasAnySlide
                              ? handleViewSlide(subtitle)
                              : handleAddMaterial(subtitle.id)
                        : undefined;
                      const frameSx = {
                        width: thumbW,
                        minWidth: isListView ? 120 : undefined,
                        height: thumbH,
                        overflow: "hidden",
                        margin: 0,
                        padding: 0,
                        flexShrink: 0,
                        position: "relative",
                        bgcolor: "background.elevated",
                        cursor: openSlideFromThumb ? "pointer" : "default",
                      };

                      // 3D models: attached cover first, live preview when it's missing or broken.
                      if (modelUrl && typeof window !== "undefined") {
                        const preview = (
                          <LoadingIframe
                            src={`${window.location.origin}/model-viewer.html?src=${encodeURIComponent(modelUrl)}&theme=${darkMode ? "dark" : "light"}`}
                            title={`3D model: ${title}`}
                            dark={darkMode}
                          />
                        );
                        return (
                          <MDBox sx={frameSx} onClick={openSlideFromThumb}>
                            {coverUrl ? (
                              <LoadingImage src={coverUrl} alt={title} dark={darkMode} fallback={preview} />
                            ) : (
                              preview
                            )}
                          </MDBox>
                        );
                      }

                      // Slides: attached cover first, then deep-zoom tiles, then a generated cover.
                      return (
                        <MDBox sx={frameSx} onClick={openSlideFromThumb}>
                          <SlideCardThumb
                            slide={primarySlide}
                            preferredUrl={coverUrl || null}
                            alt={title}
                            width={thumbW}
                            minWidth={isListView ? 120 : undefined}
                            height={thumbH}
                            emptyLabel="Empty slide"
                            dark={darkMode}
                          />
                        </MDBox>
                      );
                    })()}

                    <MDBox
                      width="100%"
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      justifyContent={isListView ? "center" : "flex-start"}
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        px: `${gapPx}px`,
                        pt: isListView ? 1.25 : embedded ? 1.25 : 0,
                        pb: isListView ? 1.25 : 0,
                        pr: showEmbeddedSlideMenu ? 5 : `${gapPx}px`,
                      }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="flex-start"
                        gap={0.5}
                        width="100%"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <MDBox sx={{ flex: 1, minWidth: 0 }}>
                          <EditableTitle
                            title={title}
                            titleFont={embedded || catalogPair ? "subtitleCatalog" : "subtitle"}
                            align={embedded || catalogPair ? "left" : "center"}
                            onEdit={(newTitle) =>
                              handleEdit(subtitle.id, newTitle, "SubTitle")
                            }
                            readOnly={readOnly}
                          />
                        </MDBox>
                        {canWriteCatalogue ? (
                          <Tooltip title="Set card thumbnail">
                            <IconButton
                              size="small"
                              aria-label="Set card thumbnail"
                              onClick={(e) => openThumbMenu(e, subtitle.id, primarySlide)}
                              sx={{
                                mt: 1.25,
                                flexShrink: 0,
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                color: "primary.main",
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                border: "1px solid",
                                borderColor: alpha(theme.palette.primary.main, 0.22),
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                                },
                              }}
                            >
                              <PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </MDBox>
                      {/* Per-slide / per-3D-model description — what
                          actually pops up over the 3D viewer in the
                          mobile app. We map it to the SubTitle's
                          `body` Localization so it survives existing
                          `handleEdit` plumbing without any extra
                          endpoints. */}
                      <EditableDescription
                        description={subtitle?.titleLocalizations?.find((e) => e?.field === "body")?.value || ""}
                        onEdit={(newValue) =>
                          handleEdit(subtitle.id, newValue, "SubBody")
                        }
                        readOnly={readOnly}
                        variant={embedded || catalogPair ? "subtitleCatalog" : "subtitle"}
                        align={embedded || catalogPair ? "left" : "center"}
                      />

                      {!embedded && canWriteCatalogue && (
                        <MDBox display="flex" gap={0.5} sx={{ my: `${gapPx}px` }}>
                          {idx !== 0 && (
                            <Tooltip title="Move left">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const body = {
                                    current_id: subtitle?.id,
                                    goal_id: item?.subtitles?.[idx - 1]?.id,
                                    lang,
                                    type: activeType,
                                  };
                                  swapTitlesAndRefreshLibrary(body);
                                }}
                                sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                              >
                                <ArrowLeftIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {item?.subtitles?.length !== idx + 1 && (
                            <Tooltip title="Move right">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const body = {
                                    current_id: subtitle?.id,
                                    goal_id: item?.subtitles?.[idx + 1]?.id,
                                    lang,
                                    type: activeType,
                                  };
                                  swapTitlesAndRefreshLibrary(body);
                                }}
                                sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                              >
                                <ArrowRightIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </MDBox>
                      )}
                      <MDBox display="flex" width="100%" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: embedded ? 0.5 : `${gapPx}px` }}>
                        {canWriteCatalogue && !embedded && (
                          <>
                            <MDButton
                              color="error"
                              variant="outlined"
                              size="small"
                              onClick={() => handleDeleteSubTitle(subtitle.id)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: catalogPair ? 15 : undefined,
                            borderRadius: catalogPair ? 999 : 3,
                            px: 1.5,
                            borderWidth: 1.5,
                            "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06) },
                          }}
                            >
                              Delete
                            </MDButton>
                            {/* AI Translate — one-click translate the
                                subtitle's card label + its description
                                (the overlay shown above the slide / 3D
                                viewer in the mobile app) into every
                                supported locale. UPSERT on the backend
                                — clicking again refreshes translations. */}
                            {showAiTranslate && (
                            <Tooltip title="AI Translate this subtitle and its description into every supported language. Re-running overwrites prior translations.">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setTranslateSubTitleTarget({
                                    ...subtitle,
                                    _displayTitle: title,
                                  })
                                }
                                sx={{
                                  borderRadius: 2,
                                  color: "primary.main",
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.06,
                                  ),
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.16,
                                    ),
                                  },
                                }}
                                aria-label="AI translate this subtitle into every supported language"
                              >
                                <TranslateRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            )}
                            {showAiTranslate && showPinTranslateControls && (
                              <Tooltip title="AI Translate all pins on this subtitle's slide(s) into every supported language. Updates existing pin translations.">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setTranslateSubTitlePinsTarget({
                                      ...subtitle,
                                      _displayTitle: title,
                                    })
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    color: "info.main",
                                    bgcolor: alpha(theme.palette.info.main, 0.06),
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.info.main,
                                        0.16,
                                      ),
                                    },
                                  }}
                                  aria-label="AI translate all pins on this subtitle into every supported language"
                                >
                                  <PushPinOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Lock / Unlock toggle (admin-only). Drives
                                titles.locked which the mobile app reads
                                in resolveSubtitleNavigationDecision to
                                gate access behind the paywall. */}
                            {showLockControls && (
                              <Tooltip
                                title={
                                  subtitle?.locked
                                    ? "Unlock — make this slide free for everyone"
                                    : "Lock — require a subscription to view this slide"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleLock(subtitle)}
                                    disabled={!!lockPending[subtitle.id]}
                                    sx={{
                                      borderRadius: 2,
                                      color: subtitle?.locked
                                        ? "warning.main"
                                        : "success.main",
                                      bgcolor: subtitle?.locked
                                        ? alpha(theme.palette.warning.main, 0.08)
                                        : alpha(theme.palette.success.main, 0.08),
                                      "&:hover": {
                                        bgcolor: subtitle?.locked
                                          ? alpha(theme.palette.warning.main, 0.16)
                                          : alpha(theme.palette.success.main, 0.16),
                                      },
                                    }}
                                  >
                                    <Icon fontSize="small">
                                      {subtitle?.locked ? "lock" : "lock_open"}
                                    </Icon>
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {/* Hide / Unhide toggle (admin-only). Sets
                                titles.is_hidden, which the backend uses
                                to filter rows out of every non-admin
                                listing endpoint (web + mobile). */}
                            {showHideControls && (
                              <Tooltip
                                title={
                                  subtitle?.is_hidden
                                    ? "Unhide — show this subtitle to all users again"
                                    : "Hide — remove this subtitle from non-admin views"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleToggleHidden(subtitle, "subtitle")
                                    }
                                    disabled={!!hidePending[subtitle.id]}
                                    sx={{
                                      borderRadius: 2,
                                      color: subtitle?.is_hidden
                                        ? "warning.main"
                                        : "info.main",
                                      bgcolor: subtitle?.is_hidden
                                        ? alpha(theme.palette.warning.main, 0.08)
                                        : alpha(theme.palette.info.main, 0.08),
                                      "&:hover": {
                                        bgcolor: subtitle?.is_hidden
                                          ? alpha(theme.palette.warning.main, 0.16)
                                          : alpha(theme.palette.info.main, 0.16),
                                      },
                                    }}
                                  >
                                    <Icon fontSize="small">
                                      {subtitle?.is_hidden
                                        ? "visibility_off"
                                        : "visibility"}
                                    </Icon>
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </>
                        )}
                        {!embedded ? (
                        <MDButton
                          color="primary"
                          variant="contained"
                          size="small"
                          onClick={() =>
                            readOnly || hasAnySlide
                              ? handleViewSlide(subtitle)
                              : handleAddMaterial(subtitle.id)
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: catalogPair ? 15 : undefined,
                            borderRadius: catalogPair ? 999 : 3,
                            px: catalogPair ? 2.2 : 1.75,
                            boxShadow: "none",
                            "&:hover": { boxShadow: theme.shadows[2] },
                          }}
                        >
                          {readOnly ? "View" : label}
                        </MDButton>
                        ) : null}
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
                {(() => {
                  const extraRefSlides =
                    ownSlides.length > 0 ? refSlides : refSlides.slice(1);
                  const renderLinkedSlideCard = (refSlide) => {
                    // Prefer private-library subtitle name ("our lib") for linked Microlab slides.
                    const slideTitle =
                      (embedded &&
                      title &&
                      title !== "Untitled" &&
                      title !== "Click to edit the sub title"
                        ? title
                        : null) ||
                      refSlide?.libraryDisplayTitle ||
                      refSlide?.slide_title ||
                      refSlide?.slideLocalizations?.find((l) => l.field === "title")?.value?.trim() ||
                      refSlide?.slide_localizations?.find((l) => l.field === "title")?.value?.trim();
                    const refLabel = slideTitle || "Slide";
                    return (
                      <MDBox sx={{ width: "100%", minWidth: 0, height: "100%" }}>
                        <Card
                          variant="outlined"
                          sx={{
                            pt: 0,
                            px: 0,
                            pb: 0,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: isListView ? "row" : "column",
                            width: "100%",
                            height: "100%",
                            borderRadius: 3,
                            borderColor: cardBorder,
                            bgcolor: cardSurfaceBg,
                            boxShadow: theme.shadows[1],
                            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                            "&:hover": {
                              boxShadow: theme.shadows[4],
                              borderColor: alpha(theme.palette.primary.main, 0.35),
                            },
                          }}
                        >
                          <SlideCardThumb
                            slide={refSlide}
                            preferredUrl={getRasterCoverUrl(refSlide, refSlide) || null}
                            alt={refLabel}
                            width={isListView ? 120 : "100%"}
                            minWidth={isListView ? 120 : undefined}
                            height={isListView ? 88 : catalogPair ? 158 : 126}
                            dark={darkMode}
                          />
                          <MDBox sx={{ px: `${gapPx}px`, py: 1.25 }}>
                            <MDTypography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                wordBreak: "break-word",
                                mb: 1.25,
                                lineHeight: 1.35,
                                minHeight: "2.7em",
                              }}
                            >
                              {refLabel}
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                              <MDButton
                                color="primary"
                                variant="contained"
                                size="small"
                                onClick={() => handleViewSlideById(refSlide)}
                                startIcon={<Icon sx={{ fontSize: 18 }}>visibility</Icon>}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderRadius: 3,
                                  px: 1.5,
                                  boxShadow: "none",
                                  "&:hover": { boxShadow: theme.shadows[2] },
                                }}
                              >
                                View
                              </MDButton>
                              {embedded && onRemoveLibraryRef && refSlide.public_title_id && (
                                <Tooltip title="Remove from library">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      onRemoveLibraryRef(
                                        refSlide.public_title_id,
                                        refSlide.id,
                                        subtitle.id
                                      )
                                    }
                                    sx={{
                                      borderRadius: 2,
                                      bgcolor: alpha(theme.palette.error.main, 0.06),
                                      "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.12) },
                                    }}
                                  >
                                    <Icon sx={{ fontSize: 20 }}>link_off</Icon>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </MDBox>
                          </MDBox>
                        </Card>
                      </MDBox>
                    );
                  };
                  if (!extraRefSlides.length) return null;
                  if (embedded && onReorderLibraryRefs && !readOnly) {
                    return (
                      <SubtitleReferencedSlidesDnD
                        subtitleId={subtitle.id}
                        slides={extraRefSlides}
                        droppableId={`lib-refs-${subtitle.id}`}
                        onReorder={onReorderLibraryRefs}
                        renderSlide={renderLinkedSlideCard}
                      />
                    );
                  }
                  return extraRefSlides.map((refSlide) => (
                    <React.Fragment key={refSlide.id}>{renderLinkedSlideCard(refSlide)}</React.Fragment>
                  ));
                })()}
                </React.Fragment>
              );
            })}
            </MDBox>
          )}
      </MDBox>
    );
  };

  const closeFolderMenu = () => setFolderMenu({ anchorEl: null, itemId: null });
  const closeSlideMenu = () =>
    setSlideMenu({
      anchorEl: null,
      subtitleId: null,
      mode: "delete",
      publicTitleId: null,
      slideId: null,
    });

  const content = (
    <>
      <Menu
        anchorEl={folderMenu.anchorEl}
        open={Boolean(folderMenu.anchorEl)}
        onClose={closeFolderMenu}
      >
        <MenuItem
          onClick={() => {
            const id = folderMenu.itemId;
            closeFolderMenu();
            if (id) handleDeleteTitle(id);
          }}
          sx={{ color: "error.main", fontWeight: 600 }}
        >
          Delete topic
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={slideMenu.anchorEl}
        open={Boolean(slideMenu.anchorEl)}
        onClose={closeSlideMenu}
      >
        {slideMenu.mode === "unlink" ? (
          <MenuItem
            onClick={() => {
              const { publicTitleId, slideId, subtitleId } = slideMenu;
              closeSlideMenu();
              if (publicTitleId && typeof onRemoveLibraryRef === "function") {
                onRemoveLibraryRef(publicTitleId, slideId, subtitleId);
              }
            }}
            sx={{ color: "error.main", fontWeight: 600 }}
          >
            Remove from library
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              const id = slideMenu.subtitleId;
              closeSlideMenu();
              if (id) handleDeleteSubTitle(id);
            }}
            sx={{ color: "error.main", fontWeight: 600 }}
          >
            Delete slide
          </MenuItem>
        )}
      </Menu>
      {slideType === "anatomy" && !embedded ? (
        <Box
          component="button"
          type="button"
          onClick={() => navigate("/anatomy-explorer")}
          sx={{
            display: "block",
            width: "100%",
            border: darkMode ? "1px solid rgba(148,163,184,0.2)" : "1px solid rgba(226,232,240,0.9)",
            p: 0,
            mb: 2,
            cursor: "pointer",
            textAlign: "left",
            color: darkMode ? "#f8fafc" : "#0f172a",
            position: "relative",
            overflow: "hidden",
            borderRadius: "28px",
            minHeight: { xs: 280, md: 320 },
            background: darkMode
              ? "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)"
              : "linear-gradient(180deg, #ffffff 0%, #f3f7ff 100%)",
            boxShadow: darkMode ? "none" : "0 16px 40px rgba(15, 23, 42, 0.06)",
            "&:hover .anatomy-hero-cta": {
              transform: "translateX(3px)",
            },
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              right: { xs: -40, md: 80 },
              top: "50%",
              transform: "translateY(-46%)",
              width: { xs: 220, md: 340 },
              height: { xs: 220, md: 340 },
              borderRadius: "50%",
              border: "1.5px solid rgba(96,165,250,0.28)",
              boxShadow: "0 0 0 28px rgba(191,219,254,0.28), 0 0 0 56px rgba(219,234,254,0.35)",
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              right: 28,
              top: 22,
              width: 72,
              height: 54,
              backgroundImage: "radial-gradient(rgba(96,165,250,0.55) 1.2px, transparent 1.3px)",
              backgroundSize: "8px 8px",
              display: { xs: "none", md: "block" },
            }}
          />
          <Box
            component="img"
            src="/images/anatomy-body.png"
            alt=""
            sx={{
              position: "absolute",
              right: { xs: -30, md: 18 },
              bottom: { xs: -8, md: -6 },
              height: { xs: 250, md: 330 },
              pointerEvents: "none",
              userSelect: "none",
              objectFit: "contain",
              objectPosition: "left bottom",
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              right: { xs: 18, md: 210 },
              top: { xs: 28, md: 54 },
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 0.75,
              bgcolor: "background.card",
              borderRadius: 999,
              px: 1.25,
              py: 0.7,
              boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
              color: "ink.body",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <Icon sx={{ fontSize: 18, color: "#4f46e5" }}>touch_app</Icon>
            Tap to explore
          </Box>
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: { xs: "70%", md: 520 }, p: { xs: 2.25, md: 3.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: "#94a3b8" }}>
                MICROLAB
              </Typography>
              <Box sx={{ width: 36, height: 1, bgcolor: "#cbd5e1" }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.045em",
                lineHeight: 0.98,
                fontSize: { xs: 34, md: 48 },
                color: "ink.strong",
              }}
            >
              Explore the
              <Box component="span" sx={{ display: "block", color: "#2563eb" }}>
                Human Body
              </Box>
              in 3D
            </Typography>
            <Typography sx={{ mt: 1.5, maxWidth: 380, color: darkMode ? "#94a3b8" : "#64748b", fontSize: 15, lineHeight: 1.45 }}>
              Explore the complete human body. Rotate, zoom, hide, and isolate structures.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", display: { xs: "none", sm: "flex" } }}>
              {[
                ["360", "Rotate 360°"],
                ["search", "Zoom in/out"],
                ["layers", "Isolate & hide"],
              ].map(([icon, label]) => (
                <Box
                  key={label}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.7,
                    px: 1.25,
                    py: 0.7,
                    borderRadius: 999,
                    bgcolor: darkMode ? "rgba(255,255,255,0.06)" : "#fff",
                    border: "1px solid rgba(148,163,184,0.28)",
                    fontSize: 13,
                    fontWeight: 700,
                    color: darkMode ? "#e2e8f0" : "#334155",
                  }}
                >
                  <Icon sx={{ fontSize: 16, color: "#64748b" }}>{icon}</Icon>
                  {label}
                </Box>
              ))}
            </Stack>
            <Box
              className="anatomy-hero-cta"
              sx={{
                mt: 2.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                background: "#2563eb",
                color: "#fff",
                borderRadius: 999,
                px: 2.25,
                py: 1.15,
                fontWeight: 800,
                fontSize: 16,
                boxShadow: "0 10px 24px rgba(37,99,235,0.28)",
                transition: "transform 0.16s ease",
              }}
            >
              Open Full-Body 3D
              <Box component="span" sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: 999, px: 1, py: 0.2, fontSize: 12, fontWeight: 800 }}>
                Free
              </Box>
              <Icon sx={{ fontSize: 20 }}>arrow_forward</Icon>
            </Box>
          </Box>
        </Box>
      ) : null}
      {!catalogPair && !hideToolbar ? (
      <MDBox py={1} mb={1} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <MDBox display="flex" alignItems="center" gap={2}>
          {canWriteCatalogue && (
            <MDButton
              color="primary"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => handleAddTitle()}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
                  {embedded ? "Add private title" : "Add Title"}
            </MDButton>
          )}
        </MDBox>
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${three_d_model ? "3D models" : "tiles"}...`}
          size="small"
          sx={{
            minWidth: 240,
            maxWidth: 360,
            width: "100%",
            "& .MuiInputBase-root": {
              borderRadius: 999,
              bgcolor: darkMode ? "rgba(255,255,255,0.08)" : "background.paper",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  sx={{ mr: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </MDBox>
      ) : null}
      {(() => {
        const galleryBody = (
          <MDBox py={0} width="100%">
            {catalogLoading ? (
              <MDBox
                sx={{
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={46} sx={{ color: catalogAccent }} />
              </MDBox>
            ) : (
              <Grid
                container
                spacing={isListView ? 1.25 : catalogPair ? 2 : 0.5}
                sx={
                  isListView
                    ? { "& > .MuiGrid-item": { width: "100%", maxWidth: "100%" } }
                    : undefined
                }
              >
                {Array.isArray(visibleItems) &&
                  visibleItems.length > 0 &&
                  visibleItems.map((item, index) => (
                    <Grid item xs={12} key={item.id}>
                      {renderItem(item, index, visibleItems)}
                    </Grid>
                  ))}
                {Array.isArray(visibleItems) && visibleItems.length === 0 && (
                  <Grid item xs={12}>
                    <MDBox
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <MDTypography variant="button">
                        {searchQuery
                          ? `No results found for "${searchQuery}".`
                          : "Nothing in this section yet."}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                )}
              </Grid>
            )}
          </MDBox>
        );

        if (!catalogPair) return galleryBody;

        return (
          <CatalogLibraryShell
            title={catalogPageTitle}
            subtitle={catalogPageSubtitle}
            darkMode={darkMode}
            segment={segment}
            onSegmentChange={selectCatalogSegment}
            categories={titleFilters}
            selectedCategoryKey={titleFilter}
            onCategoryChange={(key) => setTitleFilter(key)}
            allLabel={segment === "models" ? "All Models" : "All Slides"}
            allCount={catalogAllCount}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={
              segment === "models"
                ? "Search models, titles or description..."
                : "Search slides, titles or description..."
            }
            sortBy={catalogSortBy}
            onSortChange={setCatalogSortBy}
            viewMode={catalogViewMode}
            onViewModeChange={setCatalogViewMode}
            primaryActionLabel={canWriteCatalogue ? "Add New Section" : undefined}
            onPrimaryAction={canWriteCatalogue ? () => handleAddTitle() : undefined}
          >
            {galleryBody}
          </CatalogLibraryShell>
        );
      })()}
      {/* AI translate confirmation — parent Title row.
          Translates the title's `title` + `body` Localization rows
          into every supported app locale. UPSERT on the backend, so
          re-running this dialog overwrites prior translations
          (which is exactly the "click again to refresh" flow). */}
      <TranslateConfirmDialog
        open={!!translateTitleTarget}
        onClose={() => setTranslateTitleTarget(null)}
        title="Translate title and description"
        description={
          translateTitleTarget ? (
            <>
              The title{" "}
              <strong>
                {translateTitleTarget?.titleLocalizations?.find(
                  (e) => e?.field === "title",
                )?.value || "(untitled)"}
              </strong>{" "}
              and its description will be translated into every
              supported app language using AI.
            </>
          ) : null
        }
        targetLangsLabel="32 app languages: English, Portuguese (PT/BR), Spanish, Italian, German, Dutch, Chinese (Simplified/Traditional), Japanese, Korean, Arabic, French, Russian, Turkish, Polish, Indonesian, Vietnamese, Hindi, Persian, Romanian, Greek, Czech, Hungarian, Swedish, Danish, Norwegian, Finnish, Malay, Tagalog, Kazakh, Turkmen — source locale is skipped"
        itemSummaryLabel="Title + description"
        confirmLabel="Translate to all languages"
        onConfirm={async ({ setStatus }) => {
          setStatus("Translating title and description…");
          const res = await translateTitleCall(translateTitleTarget?.id);
          // Refresh the Redux titles list so the inline editors pick
          // up the freshly-written localizations for the active lang.
          if (res?.status === 200) await fetchTitles();
          return res;
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Translated into {res?.targetLangs?.length ?? 0} language
              {res?.targetLangs?.length === 1 ? "" : "s"}
              {typeof res?.created === "number" || typeof res?.updated === "number"
                ? ` · ${res?.created ?? 0} created, ${res?.updated ?? 0} updated`
                : ""}
              .
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(res?.targetLangs || []).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={String(code).toUpperCase()}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      />
      {/* AI translate confirmation — SubTitle (slide entry). Same
          UPSERT semantics as the parent variant, but scoped to one
          subtitle's `title` + `body` Localizations. */}
      <TranslateConfirmDialog
        open={!!translateSubTitleTarget}
        onClose={() => setTranslateSubTitleTarget(null)}
        title="Translate subtitle and description"
        description={
          translateSubTitleTarget ? (
            <>
              The subtitle{" "}
              <strong>
                {translateSubTitleTarget?._displayTitle ||
                  translateSubTitleTarget?.titleLocalizations?.find(
                    (e) => e?.field === "title",
                  )?.value ||
                  "(untitled)"}
              </strong>{" "}
              and its description will be translated into every
              supported app language using AI.
            </>
          ) : null
        }
        targetLangsLabel="32 app languages: English, Portuguese (PT/BR), Spanish, Italian, German, Dutch, Chinese (Simplified/Traditional), Japanese, Korean, Arabic, French, Russian, Turkish, Polish, Indonesian, Vietnamese, Hindi, Persian, Romanian, Greek, Czech, Hungarian, Swedish, Danish, Norwegian, Finnish, Malay, Tagalog, Kazakh, Turkmen — source locale is skipped"
        itemSummaryLabel="Subtitle + description"
        confirmLabel="Translate to all languages"
        onConfirm={async ({ setStatus }) => {
          setStatus("Translating subtitle and description…");
          const res = await translateSubTitleCall(
            translateSubTitleTarget?.id,
          );
          if (res?.status === 200) await fetchTitles();
          return res;
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Translated into {res?.targetLangs?.length ?? 0} language
              {res?.targetLangs?.length === 1 ? "" : "s"}
              {typeof res?.created === "number" || typeof res?.updated === "number"
                ? ` · ${res?.created ?? 0} created, ${res?.updated ?? 0} updated`
                : ""}
              .
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(res?.targetLangs || []).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={String(code).toUpperCase()}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      />
      {/* Bulk pin translate — parent title (all subtitles / slides). */}
      <TranslateConfirmDialog
        open={!!translateTitlePinsTarget}
        onClose={() => setTranslateTitlePinsTarget(null)}
        title="Translate all pins under this title"
        description={
          translateTitlePinsTarget ? (
            <>
              Every pin on every slide under{" "}
              <strong>
                {translateTitlePinsTarget?.titleLocalizations?.find(
                  (e) => e?.field === "title",
                )?.value || "(untitled)"}
              </strong>{" "}
              will be translated into all supported app languages using AI.
              Existing pin translations are updated.
            </>
          ) : null
        }
        targetLangsLabel="32 app languages — source locale is skipped"
        itemSummaryLabel={`${collectSlidesFromTitle(translateTitlePinsTarget || {}).length} slide(s) · ${translateTitlePinsTarget?.subtitles?.length ?? 0} subtitle(s)`}
        confirmLabel="Translate all pins"
        onConfirm={async ({ setStatus }) => {
          const entries = collectSlidesFromTitle(translateTitlePinsTarget);
          return translatePinsForSlideEntries(entries, setStatus);
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {res?.pinsTranslated ?? 0} of {res?.pinCount ?? 0} pin
              {(res?.pinCount ?? 0) === 1 ? "" : "s"} translated across{" "}
              {res?.slidesProcessed ?? 0} slide(s)
              {res?.subtitleCount
                ? ` · ${res.subtitleCount} subtitle(s)`
                : ""}
              {res?.pinsFailed ? ` · ${res.pinsFailed} failed` : ""}
              {res?.pinsSkipped ? ` · ${res.pinsSkipped} skipped (no text)` : ""}
              .
            </Typography>
            {(res?.created > 0 || res?.updated > 0) && (
              <Typography variant="caption" color="text.secondary">
                {res?.created ?? 0} created · {res?.updated ?? 0} updated.
              </Typography>
            )}
            {(res?.slideResults || []).length > 0 && (
              <Stack spacing={0.75} sx={{ maxHeight: 220, overflow: "auto" }}>
                {res.slideResults.map((row) => (
                  <Typography
                    key={row.slideId}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {row.subtitleTitle}: {row.pinsTranslated ?? 0}/
                    {row.pinCount ?? 0} pins
                    {row.pinsSkipped ? ` (${row.pinsSkipped} skipped)` : ""}
                  </Typography>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      />
      {/* Bulk pin translate — one subtitle card. */}
      <TranslateConfirmDialog
        open={!!translateSubTitlePinsTarget}
        onClose={() => setTranslateSubTitlePinsTarget(null)}
        title="Translate all pins on this subtitle"
        description={
          translateSubTitlePinsTarget ? (
            <>
              Every pin on{" "}
              <strong>
                {translateSubTitlePinsTarget?._displayTitle ||
                  translateSubTitlePinsTarget?.titleLocalizations?.find(
                    (e) => e?.field === "title",
                  )?.value ||
                  "(untitled)"}
              </strong>{" "}
              will be translated into all supported app languages using AI.
              Existing pin translations are updated.
            </>
          ) : null
        }
        targetLangsLabel="32 app languages — source locale is skipped"
        itemSummaryLabel={`${collectSlidesFromSubtitle(translateSubTitlePinsTarget || {}).length} slide(s)`}
        confirmLabel="Translate all pins"
        onConfirm={async ({ setStatus }) => {
          const entries = collectSlidesFromSubtitle(translateSubTitlePinsTarget);
          return translatePinsForSlideEntries(entries, setStatus);
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {res?.pinsTranslated ?? 0} of {res?.pinCount ?? 0} pin
              {(res?.pinCount ?? 0) === 1 ? "" : "s"} translated
              {res?.pinsFailed ? ` · ${res.pinsFailed} failed` : ""}
              {res?.pinsSkipped ? ` · ${res.pinsSkipped} skipped (no text)` : ""}
              .
            </Typography>
            {(res?.created > 0 || res?.updated > 0) && (
              <Typography variant="caption" color="text.secondary">
                {res?.created ?? 0} created · {res?.updated ?? 0} updated.
              </Typography>
            )}
          </Stack>
        )}
      />
      <input
        ref={thumbUploadInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif"
        style={{ display: "none" }}
        onChange={(event) => handleAddThumbNail(event, thumbMenu.subtitleId)}
      />
      <Menu
        anchorEl={thumbMenu.anchorEl}
        open={Boolean(thumbMenu.anchorEl)}
        onClose={closeThumbMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          disabled={thumbSaving}
          onClick={() => {
            const id = thumbMenu.subtitleId;
            setThumbMenu({ anchorEl: null, subtitleId: id, slide: null });
            setTimeout(() => thumbUploadInputRef.current?.click(), 0);
          }}
        >
          <ListItemIcon>
            <UploadFileOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Upload your own"
            secondary="Use an image from your device as the card thumbnail"
          />
        </MenuItem>
        <MenuItem
          disabled={thumbSaving || !thumbMenu.slide?.Files?.[0]?.url}
          onClick={() => {
            const { subtitleId, slide } = thumbMenu;
            closeThumbMenu();
            setThumbPicker({ open: true, subtitleId, slide });
          }}
        >
          <ListItemIcon>
            <CropFreeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Pick from slide"
            secondary={
              thumbMenu.slide?.Files?.[0]?.url
                ? "Zoom and frame a spot on the slide"
                : "Upload a slide first to pick a region"
            }
          />
        </MenuItem>
      </Menu>
      <SlideThumbnailPickerModal
        open={thumbPicker.open}
        slide={thumbPicker.slide}
        saving={thumbSaving}
        privateView={embedded}
        onClose={() =>
          setThumbPicker({ open: false, subtitleId: null, slide: null })
        }
        onCapture={async (file) => {
          const subtitleId = thumbPicker.subtitleId;
          if (!subtitleId || !file) return;
          try {
            setThumbSaving(true);
            const uploaded = await UploadMyFileRecordCall(file, "title_thumbnail");
            const thumbnail = uploaded?.url;
            if (!thumbnail) throw new Error("Upload succeeded but no image URL was returned.");
            await applySubtitleThumbnail(subtitleId, thumbnail);
            setThumbPicker({ open: false, subtitleId: null, slide: null });
          } catch (error) {
            console.error("Pick-thumbnail error:", error);
            setMessage(error?.message || "Could not save thumbnail.");
            setErrorSB(true);
          } finally {
            setThumbSaving(false);
          }
        }}
      />
      <MDSnackbar
        color="success"
        icon="check"
        title={message}
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title={message}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
      {!embedded && <Footer />}
    </>
  );

  if (embedded) return content;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
}

/*
  Inline-editable, multi-line "description" field.

  Lives on the same Localization row as `title` / `body` (the existing
  schema) — `editKind` ('MainBody' | 'SubBody') tells the parent which
  endpoint to hit (`EditTitlesCall` vs `EditSubTitlesCall`). On the
  mobile app the same string is surfaced in the floating
  `Interactive3DViewerDescription` overlay above the 3D viewer, so
  whatever an admin writes here is what students read on the model.

  Visual behaviour:
    • Read-only mode: hides itself entirely when there's no
      description, so old content stays clean.
    • Editable mode (admin / owner):
        - Empty       → muted "Add a description…" hint button.
        - Non-empty   → muted preview text.
        - Click       → expands into a multi-line textarea.
        - Blur / Save → fires `onEdit(value)`.

  We don't autosave on every keystroke because the upstream API
  re-fetches the whole title list after each save — typing 100
  characters would mean 100 GETs.
*/
/*
  When the description on a card is long, we clamp it to this many
  visual lines and surface a "Show more" / "Show less" toggle
  underneath. Two lines is the sweet spot — enough to give a
  preview without letting a paragraph hijack the card height — and
  matches the rest of the catalogue UI's body-text clamp.
*/
const DESCRIPTION_CLAMP_LINES = 2;

const EditableDescription = ({
  description,
  onEdit,
  readOnly = false,
  variant = 'title',
  align = 'center',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(description || "");
  /*
    Truncation state: `expanded` is the user's manual choice to see
    the full text, `isOverflow` is the measured truth ("does this
    text actually overflow at 2 lines?"). We only render the
    "Show more" toggle when both the description has content AND
    the text is actually overflowing — short descriptions never
    show the toggle, so the card stays clean.
  */
  const [expanded, setExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const textRef = useRef(null);

  /* When the parent finishes refetching after a save the
     `description` prop reference may change — keep the local draft
     in sync so the next "click-to-edit" starts from the latest
     server value rather than a stale draft. Resetting `expanded`
     too means a freshly edited (potentially much shorter) value
     re-checks overflow with a clean slate. */
  useEffect(() => {
    setDraft(description || "");
    setExpanded(false);
  }, [description]);

  /*
    Measure overflow whenever the rendered text changes (description
    update, variant flip, expanded toggle). We compare scrollHeight
    against clientHeight: `-webkit-line-clamp` constrains
    clientHeight to the visible 2-line box, and scrollHeight reports
    the full natural height — anything over the rounded threshold
    means there's hidden content and the toggle is warranted.
  */
  useEffect(() => {
    if (expanded) return;
    const el = textRef.current;
    if (!el) return;
    /* +1 px tolerance to absorb sub-pixel rounding in MUI/Webkit
       so a perfectly-2-line description doesn't false-positive. */
    setIsOverflow(el.scrollHeight - 1 > el.clientHeight);
  }, [description, expanded, variant, readOnly]);

  const fontSize = variant === 'subtitleCatalog'
    ? '0.8125rem'
    : variant === 'titleCatalog'
      ? '0.875rem'
      : variant === 'subtitle'
        ? '0.8125rem'
        : '0.875rem';

  const finish = () => {
    setIsEditing(false);
    const trimmed = (draft || "").trim();
    if (trimmed !== (description || "").trim()) {
      onEdit(trimmed);
    }
  };

  /*
    Shared text + toggle renderer used by both readOnly and idle
    edit-on-click branches below. Centralises the line-clamp +
    "Show more / Show less" plumbing so the two viewer experiences
    behave identically.
  */
  const renderClampedText = (text, hasContent) => (
    <>
      <MDTypography
        ref={textRef}
        variant="caption"
        sx={{
          color: hasContent ? "text.secondary" : "text.disabled",
          fontStyle: hasContent ? "normal" : "italic",
          fontSize,
          fontWeight: 400,
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
          textAlign: align,
          width: "fit-content",
          maxWidth: "100%",
          display: expanded ? "block" : "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : DESCRIPTION_CLAMP_LINES,
          overflow: expanded ? "visible" : "hidden",
        }}
      >
        {text}
      </MDTypography>
      {hasContent && (isOverflow || expanded) && (
        <MDBox
          /*
             Stop the click from bubbling up to the wrapper's
             onClick — otherwise toggling "Show more" on the
             editable variant would also flip the row into edit
             mode. The button still works inside the readOnly
             branch where the wrapper has no onClick.
          */
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((v) => !v);
            }
          }}
          sx={{
            mt: 0.5,
            display: "inline-flex",
            alignSelf: align === "left" ? "flex-start" : "center",
            cursor: "pointer",
            color: "info.main",
            fontSize: align === "left" ? "0.9rem" : "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
            textTransform: "none",
            "&:hover": {
              textDecoration: "underline",
            },
            outline: "none",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </MDBox>
      )}
    </>
  );

  if (readOnly) {
    if (!description) return null;
    return (
      <MDBox
        sx={{
          mt: 0.5,
          width: "fit-content",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: align === "left" ? "flex-start" : "center",
        }}
      >
        {renderClampedText(description, true)}
      </MDBox>
    );
  }

  if (isEditing) {
    return (
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={finish}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          /* Cmd/Ctrl-Enter saves & exits — Enter alone keeps the
             newline so authors can write paragraph descriptions
             without us hijacking line breaks. */
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            finish();
          }
          if (e.key === "Escape") {
            setDraft(description || "");
            setIsEditing(false);
          }
        }}
        autoFocus
        multiline
        minRows={2}
        maxRows={6}
        placeholder="Write a description for this model — students see this above the 3D viewer."
        size="small"
        fullWidth
        sx={{
          mt: 0.5,
          "& .MuiInputBase-input": {
            fontSize,
            lineHeight: 1.5,
          },
        }}
      />
    );
  }

  /* Idle state: muted preview + click affordance. Empty descriptions
     get a soft "Add description…" hint so admins know the field
     exists without it competing with the title for attention. */
  const hasDescription = !!(description && description.trim());
  return (
    <MDBox
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        mt: 0.5,
        width: "fit-content",
        maxWidth: "100%",
        cursor: "pointer",
        borderRadius: 1,
        px: 0.75,
        py: 0.5,
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-start" : "center",
        transition: "background-color 120ms ease",
        "&:hover": {
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
        },
      }}
    >
      {renderClampedText(
        hasDescription ? description : "Add a description…",
        hasDescription,
      )}
    </MDBox>
  );
};

export default Titles;
